import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Stack, Divider, IconButton, alpha, Chip } from '@mui/material';
import { Phone, Business, PeopleAltOutlined, Add, Person, Article, LocalOfferOutlined } from '@mui/icons-material';
import { sxConfig } from '../workspace.style';
import WorkspaceTagMenu from './WorkspaceTagMenu';
import EmailField from '../EmailField';
import companyService from '../../../../service/company.service';
import CompanyContactAddFields from '../../../Company/CompanyContactAddFields';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';

const showMissingCompanyNotification = () => {
  addNotification({
    content: 'Új kapcsolattartó nem hozható létre, mert a projekthez nincs cég rendelve.',
    type: NotificationType.WARNING,
  });
};

const WorkspaceSidebar = ({
  selectedValues,
  projectTagLabels,
  handleUpdateProjectTag,
  handleCreateTag,
  handleUpdateTag,
  handleDeleteTag,
  setCreateWorkspaceDialog,
  handleContactAdded,
}) => {
  const defCompanyContactAdd = {
    name: '',
    role: '',
    phoneNumber: '',
    email: '',
  };
  const [addContactMode, setAddContactMode] = useState(false);
  const [newContact, setNewContact] = useState(defCompanyContactAdd);

  useEffect(() => {
    if (addContactMode && !selectedValues.companyId) {
      resetContactAdd();
    }
  }, [selectedValues.companyId, addContactMode]);

  const addContact = async () => {
    if (!selectedValues.companyId) {
      showMissingCompanyNotification();

      return;
    }

    const data = {
      ...newContact,
      role: newContact.role || 'Kapcsolattartó',
    };

    const contact = await companyService.createCompanyContact(selectedValues.companyId, data);

    handleContactAdded(contact);
    resetContactAdd();
  };

  const resetContactAdd = () => {
    setAddContactMode(false);
    setNewContact(defCompanyContactAdd);
  };

  const handleAddContact = () => {
    if (!selectedValues.companyId) {
      showMissingCompanyNotification();
    }

    setAddContactMode(true);
  };

  return (
    <Paper
      sx={{
        ...sxConfig.card,
        p: 2,
      }}
    >
      {selectedValues.company && (
        <Box mb={3}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            <IconButton
              sx={{
                m: 0,
                p: 0.5,
                background: '#4ade80',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#38c96d',
                },
              }}
            >
              <Business sx={{ color: 'white' }} />
            </IconButton>
            <Typography
              fontWeight={700}
              sx={{
                overflowWrap: 'anywhere',
                minWidth: 0,
              }}
            >
              {selectedValues.company.name}
            </Typography>
          </Stack>
          <Box>
            {selectedValues.company?.phoneNumber && (
              <Stack direction="row" spacing={1} mt={1}>
                <Phone fontSize="small" />
                <Typography color="primary.main">{selectedValues.company.phoneNumber}</Typography>
              </Stack>
            )}
            {selectedValues.company?.email && (
              <Box mt={1}>
                <EmailField email={selectedValues.company.email} />
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Box mb={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton
              sx={{
                m: 0,
                p: 0.5,
                background: '#8B5CF6',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#7C3AED',
                },
              }}
            >
              <LocalOfferOutlined sx={{ color: 'white' }} />
            </IconButton>
            <Typography fontWeight={700}>Címkék</Typography>
          </Stack>
          <WorkspaceTagMenu
            labels={projectTagLabels}
            selectedTags={selectedValues.tagProjectLink}
            onTagChange={handleUpdateProjectTag}
            onTagCreate={handleCreateTag}
            onTagUpdate={handleUpdateTag}
            onTagDelete={handleDeleteTag}
          />
        </Stack>
        {selectedValues.tagProjectLink?.length > 0 && (
          <Box>
            <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap">
              {selectedValues.tagProjectLink.map((tagLink) => {
                const tag = tagLink.tag;

                return (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    sx={{
                      fontSize: 11,
                      bgcolor: alpha(tag.color, 0.2),
                      color: tag.color,
                      borderRadius: '5px',
                      fontWeight: 'bold',
                      minWidth: '20px',

                      '&:hover': {
                        bgcolor: alpha(tag.color, 0.3),
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        )}
      </Box>
      <Box mb={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton
              sx={{
                m: 0,
                p: 0.5,
                background: '#ffbb29',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#ecad24',
                },
              }}
            >
              <PeopleAltOutlined sx={{ color: 'white' }} />
            </IconButton>
            <Typography fontWeight={700}>Kapcsolatok</Typography>
          </Stack>
          <IconButton onClick={handleAddContact}>
            <Add />
          </IconButton>
        </Stack>
        {addContactMode && (
          <Box sx={{ mt: 1, borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <CompanyContactAddFields
              newContactState={{ newContact, setNewContact }}
              resetContactAdd={resetContactAdd}
              onAdd={addContact}
              variant="outlined"
              size="small"
              className="grid-cols-1 bg-transparent"
            />
          </Box>
        )}
        {selectedValues.company?.companyContact?.length > 0 && (
          <Stack mt={2} divider={<Divider sx={{ my: 2 }} />}>
            {selectedValues.company.companyContact?.map((c) => (
              <Box key={c.id}>
                <Stack direction="row" spacing={1}>
                  <Person fontSize="small" sx={{ flexShrink: 0 }} />
                  <Typography
                    fontWeight={700}
                    sx={{
                      overflowWrap: 'anywhere',
                      minWidth: 0,
                    }}
                  >
                    {c.name}
                  </Typography>
                </Stack>
                <Typography pl={3.5} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {c.role}
                </Typography>
                {c.phoneNumber && (
                  <Stack direction="row" spacing={1} mt={1}>
                    <Phone fontSize="small" />
                    <Typography color="primary.main">{c.phoneNumber}</Typography>
                  </Stack>
                )}
                {c.email && (
                  <Box mt={1}>
                    <EmailField email={c.email} />
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            sx={{
              m: 0,
              p: 0.5,
              background: '#6694f7',
              borderRadius: 2,
              '&:hover': {
                bgcolor: '#4D7FE8',
              },
            }}
          >
            <Article sx={{ color: 'white' }} />
          </IconButton>
          <Typography fontWeight={700}>Adatlapok</Typography>
        </Stack>
        <IconButton onClick={() => setCreateWorkspaceDialog({ open: true, companyId: selectedValues.companyId })}>
          <Add />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export default WorkspaceSidebar;
